import { AbstractControl, ValidatorFn } from '@angular/forms';

export function isValidEmail(email: string) {
    return email.length > 5 && email.includes('@') && email.includes('.');
}

export function isValidDomain(domain: string): boolean {
    return /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/.test(domain);
}

export function ipv4CidrValidator(): ValidatorFn {
    const ipv4CidrRegex = /^(([0-9]{1,3}\.){3}[0-9]{1,3})\/([0-9]|[1-2][0-9]|3[0-2])$/;
    return (control: AbstractControl) => {
        const value = control.value;
        if (!value || ipv4CidrRegex.test(value)) {
            return null;
        }
        return { ipv4Cidr: true };
    };
}

export function ipv6CidrValidator(): ValidatorFn {
    const ipv6CidrRegex =
        /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:))\/(\d|[1-9]\d|1[01]\d|12[0-8])$/;
    return (control: AbstractControl) => {
        const value = control.value;
        if (!value || ipv6CidrRegex.test(value)) {
            return null;
        }
        return { ipv6Cidr: true };
    };
}

export function ipCidrValidator(): ValidatorFn {
    return (control: AbstractControl) => {
        const value = control.value?.trim();
        if (!value) return null;
        const ipV4Result = isIPv4WithSubnetMask(value);
        const ipV6Result = isIPV6WithSubnet(value);

        if (!ipV4Result.error || !ipV6Result.error) return null;

        return { ipCidr: ipV4Result.message || ipV6Result.message };
    };
}

export function isIPv4WithSubnetMask(value: string): { error: boolean; message: string } {
    const match = value.trim().match(/^(\d{1,3}\.){3}\d{1,3}\/(\d{1,2})$/);
    if (!match)
        return {
            error: true,
            message: 'Invalid IP address',
        };

    const [ip, mask] = value.split('/');
    const maskNum = Number(mask);

    // Check subnet range
    if (maskNum < 8 || maskNum > 32)
        return {
            error: true,
            message: 'IP network prefix must be from /8 to /32.',
        };

    // Check each octet is 0–255
    const result = ip.split('.').every((octet) => {
        const num = Number(octet);
        return num >= 0 && num <= 255;
    });

    return result ? { error: false, message: '' } : { error: true, message: 'Invalid IP address' };
}

function isIPV6WithSubnet(str): { error: boolean; message: string } {
    const ipv6Block = /^[0-9A-F]{1,4}$/i;
    if (typeof str !== 'string') return { error: true, message: 'Invalid IP address' };

    const parts = str.split('/');
    if (parts.length !== 2) return { error: true, message: 'No subnet mask' };

    const [address, mask] = parts;
    const prefixLength = Number(mask);

    if (isNaN(prefixLength) || prefixLength < 8 || prefixLength > 128 || !/^\d{1,3}$/.test(mask)) {
        return {
            error: true,
            message: 'IP network prefix must be from /8 to /128.',
        };
    }

    let blocks = 0;
    let foundOmissionBlock = false;
    const hextets = address.split(':');

    for (let i = 0; i < hextets.length; ++i) {
        if (hextets[i] === '') {
            if (i === 0 || i === hextets.length - 1) {
                continue;
            }
            if (foundOmissionBlock)
                return {
                    error: true,
                    message: 'Invalid IP address',
                };
            foundOmissionBlock = true;
        } else if (!ipv6Block.test(hextets[i])) {
            return {
                error: true,
                message: 'Invalid IP address',
            };
        }
        blocks++;
    }

    const result = foundOmissionBlock ? blocks <= 8 : blocks === 8;

    return {
        error: !result,
        message: result ? '' : 'Invalid IP address',
    };
}
